import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ShipmentProgress } from "@/components/ShipmentProgress";
import { Package, Truck, CheckCircle2, Clock, Users, Inbox, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, icon: Icon, accent, testid, to }) => {
  const content = (
    <>
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="font-display text-3xl font-black mt-2">{value}</div>
      </div>
      <div className={`p-2 ${accent}`}><Icon className="w-6 h-6" strokeWidth={2.4} /></div>
    </>
  );
  const cls = "bg-card border border-border p-5 flex items-start justify-between";
  if (to) return <Link to={to} data-testid={testid} className={`${cls} hover:border-freight transition-colors`}>{content}</Link>;
  return <div data-testid={testid} className={cls}>{content}</div>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [myShipments, setMyShipments] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/chat/unread").then((r) => setChatUnread(r.data.count)).catch(() => {});
    if (user.role === "client") api.get("/shipments").then((r) => setMyShipments(r.data)).catch(() => {});
  }, [user.role]);

  const bs = stats?.shipments_by_status || {};

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <PageHeader
          title={user.role === "admin" ? "Centro de Control" : user.role === "driver" ? "Mis Rutas" : "Mi Panel"}
          subtitle={`Bienvenido, ${user.name}`}
        />

        {user.role === "admin" && chatUnread > 0 && (
          <Link to="/chat" data-testid="chat-alert-banner" className="flex items-center gap-3 bg-freight text-black p-4 mb-6 hover:bg-black hover:text-white transition-colors">
            <MessageSquare className="w-6 h-6" strokeWidth={2.4} />
            <div className="font-bold uppercase text-sm tracking-wide">{chatUnread} mensaje(s) nuevo(s) de clientes — pulsa para responder</div>
          </Link>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testid="stat-total" to="/shipments" label="Envíos totales" value={stats?.total_shipments ?? "—"} icon={Package} accent="bg-neutral-900 text-freight" />
          <Stat testid="stat-transit" to="/shipments?status=in_transit" label="En tránsito" value={bs.in_transit ?? 0} icon={Truck} accent="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" />
          <Stat testid="stat-delivered" to="/shipments?status=delivered" label="Entregados" value={bs.delivered ?? 0} icon={CheckCircle2} accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" />
          <Stat testid="stat-pending" to="/shipments?status=not_delivered" label="Pendientes" value={(bs.pending ?? 0) + (bs.in_transit ?? 0) + (bs.exception ?? 0)} icon={Clock} accent="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" />
        </div>

        {user.role === "admin" && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Stat testid="stat-clients" to="/users" label="Clientes" value={stats?.total_clients ?? 0} icon={Users} accent="bg-neutral-900 text-freight" />
            <Stat testid="stat-drivers" to="/users" label="Conductores" value={stats?.total_drivers ?? 0} icon={Truck} accent="bg-neutral-900 text-freight" />
          </div>
        )}

        {user.role === "client" && myShipments.length > 0 && (
          <div className="mt-8" data-testid="client-live-status">
            <h2 className="font-display text-xl font-black uppercase tracking-tight mb-4">Estado de mis envíos</h2>
            <div className="space-y-3">
              {myShipments.slice(0, 6).map((s) => (
                <Link key={s.id} to={`/shipments/${s.id}`} data-testid={`live-shipment-${s.id}`}
                  className="block bg-card border border-border p-4 hover:border-freight transition-colors">
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="font-mono font-bold text-freight">{s.tracking_number || "SIN ASIGNAR"}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.origin} → {s.destination}</div>
                  </div>
                  <ShipmentProgress status={s.status} />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {user.role === "admin" && (
            <Link to="/pickups" data-testid="quick-pickups" className="group bg-freight p-6 flex items-center justify-between hover:bg-black transition-colors">
              <div>
                <div className="font-display text-xl font-black uppercase text-black group-hover:text-white">Recogidas nuevas</div>
                <div className="text-black/70 group-hover:text-white/70 text-sm mt-1">{stats?.pending_pickups ?? 0} pendiente(s) de asignar</div>
              </div>
              <Inbox className="w-10 h-10 text-black group-hover:text-white" strokeWidth={2} />
            </Link>
          )}
          <Link to="/shipments" data-testid="quick-shipments" className="group bg-[#0A0A0A] p-6 flex items-center justify-between hover:bg-freight transition-colors">
            <div>
              <div className="font-display text-xl font-black uppercase text-white group-hover:text-black">Gestionar Envíos</div>
              <div className="text-neutral-400 group-hover:text-black/70 text-sm mt-1">Crear, asignar y rastrear paquetes</div>
            </div>
            <Package className="w-10 h-10 text-freight group-hover:text-black" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </Layout>
  );
}
